/**
 * Booking settings domain types.
 *
 * Enum string values match the events API verbatim (GET/PATCH
 * `/events/api/v1/settings/booking/`) so no value-mapping is needed — the
 * bookingApi mapper only reshapes flat snake_case ↔ nested camelCase. The five
 * dropdown option lists are served by the API in the response `options` block
 * (`settings.options.*`) — the FE no longer hardcodes them.
 */

import type { SelectOption } from "@/Shared/CSelect/CSelect";

export type ProposalExpiry = "never_expire" | "7_days" | "14_days" | "30_days";
export type ProposalDelivery =
  | "email_link"
  | "pdf_attachment"
  | "both_link_and_pdf";
export type BeoAutoCreate =
  | "when_booking_confirmed"
  | "when_proposal_signed"
  | "manual_only";
export type BeoFinalizationDeadline =
  | "7_days_before_event"
  | "14_days_before_event"
  | "21_days_before_event";
export type BeoClientReview = "yes_client_must_review" | "no_internal_only";

export interface ProposalSettings {
  expiry: ProposalExpiry;
  delivery: ProposalDelivery;
  headerText: string;
  emailTemplate: string;
  requireESignature: boolean;
  requireDeposit: boolean;
  allowRevisions: boolean;
  versionHistory: boolean;
}

export interface BeoSettings {
  autoCreate: BeoAutoCreate;
  finalizationDeadline: BeoFinalizationDeadline;
  clientReviewRequired: BeoClientReview;
  separateBeoPerTeam: boolean;
  lockAfterFinalisation: boolean;
}

/** API-served dropdown option lists for this screen (read-only). */
export interface BookingOptions {
  proposalExpiry: SelectOption[];
  proposalDelivery: SelectOption[];
  autoCreate: SelectOption[];
  beoFinalizationDeadline: SelectOption[];
  clientReviewRequired: SelectOption[];
}

export interface BookingSettings {
  proposal: ProposalSettings;
  beo: BeoSettings;
  /** Dropdown choices from the API `options` block. */
  options: BookingOptions;
}

/** Insertable merge variables shown as chips above the email template. */
export const TEMPLATE_VARIABLES = [
  "{{customer_name}}",
  "{{total_price}}",
  "{{venue_name}}",
] as const;

const DEFAULT_EMAIL_TEMPLATE = `Hello {{customer_name}}

Thank you for your interest in hosting your event with us.
We are pleased to share your event proposal. It includes all the details such as venue, package, pricing, and available options tailored to your requirements.
Please review the proposal at your convenience and let us know if you would like any changes or adjustments. Once everything looks good, you can confirm your booking directly from the proposal page.
If you have any questions, feel free to reach out — we're here to help.
Looking forward to hosting your event.

Best regards,
{{venue_name}} Team`;

export function defaultBookingSettings(): BookingSettings {
  return {
    proposal: {
      expiry: "14_days",
      delivery: "email_link",
      headerText:
        "Thank you for choosing {{venue_name}} for your special event.",
      emailTemplate: DEFAULT_EMAIL_TEMPLATE,
      requireESignature: true,
      requireDeposit: true,
      allowRevisions: true,
      versionHistory: true,
    },
    beo: {
      autoCreate: "when_booking_confirmed",
      finalizationDeadline: "14_days_before_event",
      clientReviewRequired: "yes_client_must_review",
      separateBeoPerTeam: true,
      lockAfterFinalisation: true,
    },
    // Options come from the API; empty on the read-failure fallback path.
    options: {
      proposalExpiry: [],
      proposalDelivery: [],
      autoCreate: [],
      beoFinalizationDeadline: [],
      clientReviewRequired: [],
    },
  };
}
